// Generated by tutorials/record.cjs from tutorials/articles/h29-adjustments.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h29 --text-only` (wording) or
// `node tutorials/record.cjs h29` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'adjustments',
  catalogId: 'h29',
  title: 'Adjustments',
  summary: 'An adjustment changes what a resident gets from the menu: it keeps a food off the tray, swaps it for another, or changes how it is served. Add one for a resident, or a global adjustment your facility shares.',
  audience: 'Dietary managers and staff who keep residents\' food preferences and restrictions',
  area: 'residents',
  learn: [
    'What an adjustment does, and how it differs from a meal pattern',
    'The types: item, food group, menu category, allergy group and mealtime',
    'What each action does: Never Serve, Replace With, Next Available, Modify and the others',
    'Add an adjustment for one resident, or a global adjustment',
    'Change, pause or delete an adjustment',
    'Check the result on the tray ticket',
  ],
  keywords: [
    'adjustment',
    'adjustments',
    'never serve',
    'replace with',
    'substitute',
    'substitution',
    'next available',
    'modify',
    'request',
    'consistency override',
    'allow',
    'never serve when',
    'diet override',
    'temp preference',
    'dislike',
    'preference',
    'global adjustment',
    'add global',
    'break link',
    'priority',
    'deactivate',
    'tray ticket',
    'visualizer',
  ],
  steps: [
    {
      id: 'open',
      part: 'Read a resident\'s adjustments',
      heading: 'Open Adjustments',
      text: 'Open the resident and select Adjustments on the left. An adjustment changes what the resident gets from the menu: it keeps an item off the tray, swaps it for another, or changes how it is served.\n\nThe list is grouped by action. Each row shows its type, what it applies to, the meals it is on (B, L and S), and what it changes the item to.',
      media: {
        image: {
          src: '/help/adjustments/open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/adjustments/open.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.1,
        },
        alt: 'The Adjustments section for Haddad, Fatima: Next Available for the item Scrambled Eggs at breakfast, Modify for the Entree menu category at lunch and supper, and Never Serve for the FISH allergy group.',
      },
    },
    {
      id: 'allergy-row',
      part: 'Read a resident\'s adjustments',
      heading: 'Allergies add their own',
      text: 'Fatima is allergic to fish, so her list has a Never Serve for the FISH allergy group: nothing in that group is served to her. Dietech added it when her allergy was saved, because the allergy is linked to that adjustment. See Allergies, likes and dislikes.\n\nThe globe icon means it is a global adjustment, shared with the other residents who have it.',
      media: {
        image: {
          src: '/help/adjustments/allergy-row.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Never Serve row for the FISH allergy group, highlighted, with the globe icon.',
      },
    },
    {
      id: 'type',
      part: 'Add one for this resident',
      heading: 'Start a new adjustment and pick its type',
      text: 'Select the arrow next to Add Global, then New (This Resident Only). Type says what the adjustment applies to:\n\nItem is one food. Food Group is every food in a group, such as all pork. Menu Category is one part of the meal, such as every dessert. Allergy Group is every food marked with that allergen. Mealtime is the whole meal.',
      media: {
        image: {
          src: '/help/adjustments/type.webp',
          width: 1280,
          height: 742,
        },
        clip: {
          src: '/help/adjustments/type.mp4',
          width: 1280,
          height: 742,
          durationSec: 3.7,
        },
        alt: 'The New Adjustment window with the Type list open: Allergy Group, Food Group, Item, Mealtime and Menu Category.',
      },
    },
    {
      id: 'action',
      part: 'Add one for this resident',
      heading: 'Pick the action',
      text: 'Pick Item and search for the food. Then Action says what happens to it. The ones you will use most:\n\nNever Serve keeps it off the tray. Replace With serves an item you pick in its place. Next Available takes it off so the next choice on the menu is served instead. Modify keeps the item but changes its consistency, portion, size or service item.',
      media: {
        image: {
          src: '/help/adjustments/action.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/adjustments/action.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The window with Type Item and Item Mashed Potatoes, and the Action list open: Allow, Consistency Override, Modify, Never Serve, Never Serve When, Next Available, Replace With and Request.',
      },
    },
    {
      id: 'more-actions',
      part: 'Add one for this resident',
      heading: 'The other actions',
      text: 'Request moves an item that is already on the menu, such as an alternate, up to be the resident\'s first choice. Consistency Override serves the item in another consistency. Allow lets the resident have an item their diet would otherwise leave off.\n\nNever Serve When keeps the item off only when something else is on the same meal, which you pick on the On Menu tab. For example, no gravy when the entree already comes with sauce.',
      media: {
        image: {
          src: '/help/adjustments/more-actions.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The open Action list, pointing at Request at the bottom.',
      },
    },
    {
      id: 'replacement',
      part: 'Add one for this resident',
      heading: 'Pick what to serve instead',
      text: 'Pick Replace With. On the Modifications tab, search for the Replacement Item. Fatima gets buttered rice whenever mashed potatoes are on her menu. You can also change the replacement\'s size, consistency, portion or service item here.',
      media: {
        image: {
          src: '/help/adjustments/replacement.webp',
          width: 1280,
          height: 742,
        },
        clip: {
          src: '/help/adjustments/replacement.mp4',
          width: 1280,
          height: 742,
          durationSec: 6,
        },
        alt: 'The window with Action Replace With and the Modifications tab showing Replacement Item Buttered Rice.',
      },
    },
    {
      id: 'meals',
      part: 'Add one for this resident',
      heading: 'Choose the meals',
      text: 'Under Mealtime, check the meals the adjustment is for. Mashed potatoes are not on the breakfast menu, so clear Breakfast.\n\nFor some days of the week, or a start and end date, use the Days tab, as Allergies, likes and dislikes shows.',
      media: {
        image: {
          src: '/help/adjustments/meals.webp',
          width: 1280,
          height: 742,
        },
        clip: {
          src: '/help/adjustments/meals.mp4',
          width: 1280,
          height: 742,
          durationSec: 4,
        },
        alt: 'The Mealtime boxes with Lunch and Supper checked and Breakfast cleared.',
      },
    },
    {
      id: 'save',
      part: 'Add one for this resident',
      heading: 'Save',
      text: 'Select Save. The adjustment is listed under Replace With, with the item it replaces, the meals and the item served instead.',
      media: {
        image: {
          src: '/help/adjustments/save.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/adjustments/save.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.7,
        },
        alt: 'The Adjustments list with a new Replace With row: Item Mashed Potatoes at lunch and supper, replaced by Buttered Rice.',
      },
    },
    {
      id: 'add-global',
      part: 'Add a global adjustment',
      heading: 'Open the global list',
      text: 'Select Add Global. You see your facility\'s global adjustments, grouped by action, each with its group, name and meals. Pick a group or type in Find to narrow the list.\n\nOne the resident already has is marked Linked.',
      media: {
        image: {
          src: '/help/adjustments/add-global.webp',
          width: 1280,
          height: 704,
        },
        clip: {
          src: '/help/adjustments/add-global.mp4',
          width: 1280,
          height: 704,
          durationSec: 2,
        },
        alt: 'The Global Adjustments window for Haddad, Fatima, listing allergy adjustments, Decaf Instead of Coffee, Herbal Tea Instead of Coffee, Large Entree and No Dessert at Supper, with Fish Allergy marked Linked.',
      },
    },
    {
      id: 'pick-global',
      part: 'Add a global adjustment',
      heading: 'Check the ones you want and add them',
      text: 'Check the Insert box on each adjustment you want, then select Add.',
      media: {
        image: {
          src: '/help/adjustments/pick-global.webp',
          width: 1280,
          height: 704,
        },
        clip: {
          src: '/help/adjustments/pick-global.mp4',
          width: 1280,
          height: 704,
          durationSec: 4.2,
        },
        alt: 'Decaf Instead of Coffee checked in the Insert column, and the Add (1) button.',
      },
    },
    {
      id: 'global-row',
      part: 'Add a global adjustment',
      heading: 'The global adjustment is on the list',
      text: 'It is added with the globe icon and stays linked to the global adjustment. A change to the global one reaches every resident who has it, except for anything you changed for this resident.\n\nTo make it this resident\'s own, select it, then More actions and Global: Break Link. Global: Reset puts it back the way the global one is.',
      media: {
        image: {
          src: '/help/adjustments/global-row.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/adjustments/global-row.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.1,
        },
        alt: 'The Adjustments list with Decaf Instead of Coffee added under Replace With: Coffee replaced by Decaf Coffee, marked with the globe icon.',
      },
    },
    {
      id: 'edit',
      part: 'Change, pause or remove one',
      heading: 'Change an adjustment',
      text: 'Select the adjustment, then Open. The window is the same as for a new one: change what you need and select Save.\n\nIf you change what a global adjustment applies to, Dietech warns you that saving makes it this resident\'s own.',
      media: {
        image: {
          src: '/help/adjustments/edit.webp',
          width: 1280,
          height: 742,
        },
        clip: {
          src: '/help/adjustments/edit.mp4',
          width: 1280,
          height: 742,
          durationSec: 2,
        },
        alt: 'The Edit Adjustment window for the Replace With adjustment: Item Mashed Potatoes, Action Replace With, Lunch and Supper checked, and Replacement Item Buttered Rice.',
      },
    },
    {
      id: 'deactivate',
      part: 'Change, pause or remove one',
      heading: 'Pause an adjustment',
      text: 'To stop an adjustment for a while without losing it, select it and then Deactivate. It stays on the list, struck through, and is not applied until you select it and Activate it again.',
      media: {
        image: {
          src: '/help/adjustments/deactivate.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/adjustments/deactivate.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.9,
        },
        alt: 'The Next Available adjustment for Scrambled Eggs struck through as inactive, with 4 active and 1 inactive beside the title.',
      },
    },
    {
      id: 'delete',
      part: 'Change, pause or remove one',
      heading: 'Delete an adjustment',
      text: 'Select it, then More actions and Delete, and confirm. Deleted Items, in the same menu, lists the adjustments that were deleted.\n\nAn adjustment an allergy added keeps the allergen off the tray, so delete it only when the allergy itself has been removed.',
      media: {
        image: {
          src: '/help/adjustments/delete.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/adjustments/delete.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.7,
        },
        alt: 'The Delete Adjustment confirmation asking to delete the Next Available adjustment for Scrambled Eggs, with Cancel and Delete.',
      },
    },
    {
      id: 'ticket',
      part: 'Check the tray ticket',
      heading: 'Check the tray ticket',
      text: 'Select Tray Ticket Visualizer and pick a meal. At lunch Fatima\'s ticket has buttered rice where the menu has mashed potatoes, marked as a substitution, and her entree is a large portion.',
      media: {
        image: {
          src: '/help/adjustments/ticket.webp',
          width: 1280,
          height: 794,
        },
        clip: {
          src: '/help/adjustments/ticket.mp4',
          width: 1280,
          height: 794,
          durationSec: 6,
        },
        alt: 'The Tray Ticket Visualizer at lunch for Haddad, Fatima: Roast Turkey with Gravy in a large portion and Buttered Rice marked as a substitution in place of Mashed Potatoes.',
      },
    },
  ],
  tips: [
    'An adjustment only changes items that are on the menu. To give a resident something the menu does not have, such as decaf coffee at every breakfast, add it to their meal pattern instead. See Meal patterns.',
    'When the same thing suits many residents, such as decaf instead of coffee, use a global adjustment. You keep it in one place and add it to each resident with Add Global.',
    'Write the reason in the Notes tab, such as "dislikes, per care conference", so the next person knows why the adjustment is there.',
  ],
  notes: [
    'Adjustments are applied in order within each action. When two of the same action could change the same item, use the up and down arrows on the toolbar to change which comes first.',
    'A menu category has a few actions of its own. Temp Preference - Hot or Cold serves the hot or the cold choice in that category. Diet Override judges that category, or a whole meal for the Mealtime type, by another diet order, picked on the Diet Order tab. Modify for a menu category can also give the resident the first two or three choices in it, with Multiples.',
    'Request for a menu category turns on a category your menu only serves when it is asked for.',
    'Adding, changing and deleting adjustments, and adding global ones, each need the right role. If a button is greyed out, ask your administrator.',
  ],
  related: ['allergies-likes-dislikes', 'meal-patterns', 'tray-tickets'],
  recorded: '2026-10-28',
};

export default article;
